import type {
  QaUncleanDisconnectOptions,
  WebSocketService,
} from './websocket-service';

/** Vite dev, or platform-dev production build — not staging/production. */
export function isCapWsDebugEnvironment(): boolean {
  return (
    import.meta.env.DEV ||
    (typeof window !== 'undefined' &&
      window.location.hostname === 'platform-dev.capitol.ai')
  );
}

export type CapWsDebugSimulateOptions =
  | string
  | number
  | QaUncleanDisconnectOptions;

function normalizeSimulateOptions(
  options?: CapWsDebugSimulateOptions,
): QaUncleanDisconnectOptions {
  if (options === undefined) {
    return {};
  }
  if (typeof options === 'string') {
    return { reason: options };
  }
  if (typeof options === 'number') {
    return { holdMs: options };
  }
  return options;
}

export interface CapWsDebugApi {
  /** Print usage in the console. */
  help: () => void;
  /**
   * Clean disconnect (`shouldReconnect = false`). Skips the Reconnecting toast
   * and goes straight to Checking Status / polling.
   */
  disconnect: (reason?: string) => void;
  /**
   * Abnormal socket close with reconnection still enabled — matches a real
   * network drop: Reconnecting → Checking Status → (poll / Disconnected / Run Complete).
   * Full doc: platform-frontend/docs/workflow-execution-websocket-flow.md#manual-qa-__capwsdebug
   * @example
   * // ~15s Reconnecting with advancing counter (1/14 → 2/14 → …)
   * __capWsDebug.simulateUncleanDisconnect(15000)
   * __capWsDebug.simulateUncleanDisconnect({ holdMs: 15000 })
   *
   * @example
   * // 15s block + 20s more failed retries before a successful connect
   * __capWsDebug.simulateUncleanDisconnect({ holdMs: 15000, blockReconnectMs: 20000 })
   */
  simulateUncleanDisconnect: (options?: CapWsDebugSimulateOptions) => void;
  /** Browser offline event (WS pauses reconnect attempts). */
  goOffline: () => void;
  /** Browser online event (may resume reconnect if `shouldReconnect` is still true). */
  goOnline: () => void;
  isConnected: () => boolean;
  getReconnectStatus: () => ReturnType<WebSocketService['getReconnectStatus']>;
}

function simulateUncleanDisconnect(
  service: WebSocketService,
  options?: CapWsDebugSimulateOptions,
): void {
  const opts = normalizeSimulateOptions(options);
  console.log('[__capWsDebug] simulateUncleanDisconnect:', {
    ...opts,
    url: service.getCurrentUrl(),
    isConnected: service.isConnected(),
    reconnect: service.getReconnectStatus(),
    timestamp: new Date().toISOString(),
  });

  service.simulateUncleanDisconnectForQA(opts);
}

function printHelp(): void {
  console.info(
    [
      '',
      '%c__capWsDebug%c — workflow WebSocket QA helpers',
      'font-weight:bold',
      '',
      '',
      'Environments: localhost (vite dev) and https://platform-dev.capitol.ai only.',
      '',
      'Setup:',
      '  1. Open a workflow in the editor.',
      '  2. Click Run and wait until the run is active (timer / Stop visible).',
      '',
      'Recommended — full disconnect flow (Reconnecting → Checking Status):',
      '  __capWsDebug.simulateUncleanDisconnect()',
      '',
      'Longer Reconnecting toast + advancing counter (localhost is <1s by default):',
      '  __capWsDebug.simulateUncleanDisconnect(15000)',
      '  __capWsDebug.simulateUncleanDisconnect({ holdMs: 15000, blockReconnectMs: 20000 })',
      '',
      'Alternate — skip Reconnecting (straight to Checking Status / poll):',
      '  __capWsDebug.disconnect()',
      '',
      'Network simulation:',
      '  __capWsDebug.goOffline()',
      '  __capWsDebug.goOnline()',
      '',
      'Inspect state:',
      '  __capWsDebug.isConnected()',
      '  __capWsDebug.getReconnectStatus()',
      '',
      'Expected toasts while disconnected during a run:',
      '  • Reconnecting (X/Y) — after simulateUncleanDisconnect, while WS retries',
      '  • Checking Status. Verifying run status (X/20)... — while polling',
      '  • Disconnected. Run continues on server... — after 20 polls, run still active',
      '  • Run Complete — when the API reports success/failed/stopped (short runs)',
      '',
      'Docs: platform-frontend/docs/workflow-execution-websocket-flow.md#manual-qa-__capwsdebug',
      '',
    ].join('\n'),
  );
}

function createCapWsDebugApi(service: WebSocketService): CapWsDebugApi {
  return {
    help: printHelp,
    disconnect: (reason = 'Debug disconnect') => service.disconnect(reason),
    simulateUncleanDisconnect: (options) =>
      simulateUncleanDisconnect(service, options),
    goOffline: () => window.dispatchEvent(new Event('offline')),
    goOnline: () => window.dispatchEvent(new Event('online')),
    isConnected: () => service.isConnected(),
    getReconnectStatus: () => service.getReconnectStatus(),
  };
}

/**
 * Exposes `window.__capWsDebug` for manual workflow status / toast QA.
 * No-op outside localhost dev and platform-dev.capitol.ai.
 */
export function registerCapWsDebug(service: WebSocketService): void {
  if (!isCapWsDebugEnvironment()) {
    return;
  }

  const api = createCapWsDebugApi(service);
  window.__capWsDebug = api;

  // Common console typo: single underscore — alias with a nudge.
  Object.defineProperty(window, '_capWsDebug', {
    get() {
      console.warn(
        '[CapAI] Use __capWsDebug (double underscore), not _capWsDebug.',
      );
      return api;
    },
    configurable: true,
  });

  console.info(
    '%c[CapAI]%c Workflow WS debug helpers: type __capWsDebug.help()',
    'font-weight:bold;color:#3b82f6',
    '',
  );
}

declare global {
  interface Window {
    __capWsDebug?: CapWsDebugApi;
  }
}
